import React, { type ComponentProps } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Text from '../common/Text';
import { HOW_IT_WORKS, HowItWorksStep } from '../../data/landingData';

/** Icon glyph size; icon box matches title line height (see `iconCircle`). */
const STEP_ICON_GLYPH = 15;

type ThemeColors = {
  text: string;
  secondary: string;
  card: string;
  border: string;
};

type Props = {
  colors: ThemeColors;
  isMobile: boolean;
};

const StepCard: React.FC<{
  step: HowItWorksStep;
  isLast: boolean;
  isMobile: boolean;
  colors: ThemeColors;
}> = ({ step, isLast, isMobile, colors }) => (
  <View style={[styles.stepWrapper, isMobile && styles.stepWrapperMobile]}>
    <View
      style={[
        styles.stepCard,
        isMobile && styles.stepCardMobile,
        {
          backgroundColor: colors.card,
          borderColor: colors.border,
          ...(Platform.OS === 'web'
            ? { boxShadow: `0 4px 24px 0 ${step.color}22` }
            : {
                shadowColor: step.color,
                shadowOpacity: 0.15,
                shadowRadius: 16,
                shadowOffset: { width: 0, height: 4 },
                elevation: 4,
              }),
        },
      ]}
    >
      {/* Step number pill */}
      <View style={[styles.stepPill, { backgroundColor: step.color + '18' }]}>
        <Text style={[styles.stepNumber, { color: step.color }]}>
          {step.step}
        </Text>
      </View>

      {/* One line: small icon (same height as title) + title — no wrap */}
      <View style={styles.stepHeadingRow}>
        <View
          style={[styles.iconCircle, { backgroundColor: step.color + '18' }]}
        >
          <Ionicons
            name={step.icon as ComponentProps<typeof Ionicons>['name']}
            size={STEP_ICON_GLYPH}
            color={step.color}
          />
        </View>
        <Text
          style={[styles.stepTitle, { color: colors.text }]}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {step.title}
        </Text>
      </View>

      {/* Body starts at the same vertical offset in every card */}
      <Text style={[styles.stepBody, { color: colors.secondary }]}>
        {step.body}
      </Text>
    </View>

    {/* Connector arrow (hidden on last card and on mobile) */}
    {!isLast && !isMobile && (
      <View style={styles.connector}>
        <Ionicons
          name="arrow-forward"
          size={22}
          color={colors.secondary}
          style={{ opacity: 0.4 }}
        />
      </View>
    )}
  </View>
);

const HowItWorksSection: React.FC<Props> = ({ colors, isMobile }) => {
  return (
    <View style={[styles.section, isMobile && styles.sectionMobile]}>
      <View style={[styles.header, isMobile && styles.headerMobile]}>
        <Text style={[styles.eyebrow, { color: colors.secondary }]}>
          Simple as 1-2-3
        </Text>
        <Text
          style={[
            styles.title,
            isMobile && styles.titleMobile,
            { color: colors.text },
          ]}
        >
          How ColourSense Works
        </Text>
        <Text
          style={[
            styles.subtitle,
            isMobile && styles.subtitleMobile,
            { color: colors.secondary },
          ]}
        >
          From selfie to seasonal palette in under 30 seconds.
        </Text>
      </View>

      <View style={[styles.steps, isMobile && styles.stepsMobile]}>
        {HOW_IT_WORKS.map((step, i) => (
          <StepCard
            key={step.step}
            step={step}
            isLast={i === HOW_IT_WORKS.length - 1}
            isMobile={isMobile}
            colors={colors}
          />
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    paddingHorizontal: 24,
    paddingVertical: 40,
  },
  sectionMobile: {
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 32,
    width: '100%',
    flexGrow: 0,
  },
  header: {
    alignItems: 'center',
    marginBottom: 28,
  },
  headerMobile: {
    marginBottom: 18,
  },
  eyebrow: {
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 2,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: '900',
    textAlign: 'center',
    letterSpacing: -0.8,
    lineHeight: 34,
    marginBottom: 8,
  },
  titleMobile: {
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: -0.4,
  },
  subtitle: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    opacity: 0.8,
  },
  subtitleMobile: {
    fontSize: 14,
    lineHeight: 21,
  },
  steps: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    gap: 12,
  },
  stepsMobile: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    alignContent: 'flex-start',
    width: '100%',
    flexGrow: 0,
    gap: 16,
  },
  stepWrapper: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: 'auto',
    minWidth: 240,
    maxWidth: 360,
  },
  stepWrapperMobile: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    maxWidth: 99999,
    width: '100%',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  stepCard: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 'auto',
    minWidth: 0,
    minHeight: 0,
    borderRadius: 20,
    borderWidth: 1,
    padding: 24,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
  stepCardMobile: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    width: '100%',
    alignSelf: 'stretch',
    padding: 16,
    minHeight: 0,
  },
  stepPill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 100,
    marginBottom: 10,
    alignSelf: 'flex-start',
  },
  stepNumber: {
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 1,
  },
  stepHeadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    minHeight: 22,
    marginBottom: 12,
  },
  iconCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  stepTitle: {
    flex: 1,
    minWidth: 0,
    fontSize: 16,
    fontWeight: '800',
    letterSpacing: -0.3,
    lineHeight: 22,
  },
  stepBody: {
    fontSize: 14,
    lineHeight: 21,
    opacity: 0.85,
    width: '100%',
  },
  connector: {
    width: 40,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
  },
});

export default HowItWorksSection;
